import { OnEventTriggered, OnEventTriggeredEvent } from './EventStrategy';
import { PageEvent, PageEventStrategy, PageEventProps } from './PageEventStrategy';
import { mock } from 'jest-mock-extended';
import { contextFactory } from '../../../__tests__/factories';

describe('PageEventStrategy', () => {
  let pageEventStrategy: PageEventStrategy;
  let mockObserver: jest.Mock<MutationObserver, [MutationCallback]>;
  let onTriggerEventMock: jest.Mock<
    OnEventTriggered<PageEventProps, PageEvent>,
    [OnEventTriggeredEvent<PageEventProps, PageEvent>]
  >;
  beforeEach(() => {
    onTriggerEventMock = jest.fn<
      OnEventTriggered<PageEventProps, PageEvent>,
      [OnEventTriggeredEvent<PageEventProps, PageEvent>]
    >();

    mockObserver = jest
      .fn<MutationObserver, [MutationCallback]>()
      .mockImplementation(() => mock<MutationObserver>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    global.MutationObserver = mockObserver;
  });

  function fireMutationObserver() {
    const instance = mockObserver.mock.instances[0];
    const cb = mockObserver.mock.calls[0][0];
    cb([], instance);
  }

  function getInstance(props: PageEventProps): PageEventStrategy {
    const context = contextFactory.build();
    const instance = new PageEventStrategy(context, props);
    instance.onEventTriggered = onTriggerEventMock;
    return instance;
  }

  type Case = {
    name: string;
    /**
     * For detecting url changes after the event is loaded.
     * This essentially executes the mutation observer.
     */

    urls: string[];
    props: PageEventProps;
    expectedTriggerCount: number;
  };

  function setUrl(url: string) {
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    delete window.location;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    window.location = new URL(url);
  }

  // JSDOM does not allow us to modify location, see https://stackoverflow.com/a/60110508/400861
  const realLocation = window.location;
  afterEach(() => {
    window.location = realLocation;
  });

  const defaultUrl = 'https://samelogic.com/valid/url';
  const defaultProps: PageEventProps = {
    event: 'PageView',
    urlPattern: defaultUrl
  };
  describe.each<Case>([
    {
      name: 'when url matches on start',
      urls: [defaultUrl],
      props: defaultProps,
      expectedTriggerCount: 1
    },
    {
      name: 'when url matches regex on start with query param',
      urls: [defaultUrl, 'https://samelogic.com/?gtm_debug=x'],
      props: { ...defaultProps, urlPattern: 'https://samelogic.com/*' },
      expectedTriggerCount: 2
    },
    {
      name: 'when url matches regex on start',
      urls: [defaultUrl],
      props: { ...defaultProps, urlPattern: 'https://samelogic.com/*/url' },
      expectedTriggerCount: 1
    },
    {
      name: 'when url does not match on start',
      urls: [defaultUrl],
      props: { ...defaultProps, urlPattern: 'https://samelogic.com/*/wrong-url' },
      expectedTriggerCount: 0
    },
    {
      name: 'when url matches after started',
      urls: [defaultUrl, 'https://samelogic.com/new-url/url'],
      props: { ...defaultProps, urlPattern: 'https://samelogic.com/new-url/url' },
      expectedTriggerCount: 1
    },
    {
      name: 'when url matches regex after started',
      urls: [defaultUrl, 'https://samelogic.com/url/new-url'],
      props: { ...defaultProps, urlPattern: 'https://samelogic.com/*/new-url' },
      expectedTriggerCount: 1
    },
    {
      name: 'when url does not match after started',

      urls: [defaultUrl, 'https://samelogic.com/new-url/url'],
      props: { ...defaultProps, urlPattern: 'https://samelogic.com/invalid/url' },
      expectedTriggerCount: 0
    }
  ])('$name', ({ urls, props, expectedTriggerCount }) => {
    beforeEach(() => {
      pageEventStrategy = getInstance(props);
      pageEventStrategy.start();

      for (const url of urls) {
        setUrl(url);
        fireMutationObserver();

        // Aded a second fire to ensure we trigger the event only when the
        // url has changed. Since we are using MutationObserver, this will
        // fire multiple times without the URL changing.
        fireMutationObserver();
      }
    });

    it(`should ${expectedTriggerCount > 0 ? '' : 'not '}execute the trigger`, () => {
      expect(onTriggerEventMock).toHaveBeenCalledTimes(expectedTriggerCount);
      if (expectedTriggerCount > 0) {
        expect(onTriggerEventMock).toBeCalledWith(
          expect.objectContaining({
            condition: true,
            props: props,
            data: { href: urls[urls.length - 1] }
          })
        );
      }
    });
  });
});
